
import { Button, Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";

const mergeProjectForView = (currentProject, apiProject) => {
  const base = currentProject || {};
  const incoming = apiProject || {};

  return {
    ...base,
    ...incoming,
    projectId: incoming.projectId || base.projectId || '',
    title: incoming.title || base.title || base.label || incoming.label || '',
    label: incoming.label || base.label || incoming.title || base.title || '',
    description1: incoming.description1 || base.description1 || '',
    description2: incoming.description2 || base.description2 || '',
    manual: incoming.manual || base.manual || null,
    green: incoming.green || base.green || null,
    amber: incoming.amber || base.amber || null,
    red: incoming.red || base.red || null,
    total: incoming.total || base.total || null,
    attempts: Array.isArray(incoming.attempts) ? incoming.attempts : (Array.isArray(base.attempts) ? base.attempts : []),
  };
};

export default function ProjectDetails({ activeProject, setActiveProject, userPathway, onBack, initialAttemptId = '' }) {
  const { user: authUser } = useSelector(x => x.auth);
  // console.log('ProjectDetails userPathway:', userPathway); // Debug log
  const [attempts, setAttempts] = useState(activeProject.attempts || []);
  const [viewMode, setViewMode] = useState('details');
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [pendingInitialAttemptId, setPendingInitialAttemptId] = useState(() => String(initialAttemptId || '').trim());
  const isMountedRef = useRef(true);
  // Local state for DatePicker value
  const [localCompletedAt, setLocalCompletedAt] = useState(() => {
    const val = activeProject.completedAt;
    if (typeof val === 'string') {
      const dateObj = new Date(val);
      if (!isNaN(dateObj.getTime())) return dateObj;
    }
    return null;
  });
  const url = process.env.REACT_APP_URL;

  // Fetch project data (restored)
  const fetchProjectData = async () => {
    try {
      const response = await fetch(`${url}/user/${userPathway.user}/userPathway/${userPathway._id}/project/${activeProject.projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch project data');
      }
      const updatedProject = await response.json();
      if (!isMountedRef.current) return;
      const mergedProject = mergeProjectForView(activeProject, updatedProject);
      setActiveProject(mergedProject);
      setAttempts(Array.isArray(mergedProject.attempts) ? mergedProject.attempts : []);
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    if (activeProject && activeProject.projectId) {
      fetchProjectData();
    }
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.projectId]);

  useEffect(() => {
    setViewMode('details');
    setSelectedAttempt(null);
  }, [activeProject?.projectId]);

  useEffect(() => {
    setPendingInitialAttemptId(String(initialAttemptId || '').trim());
  }, [initialAttemptId, activeProject?.projectId]);

  useEffect(() => {
    if (!pendingInitialAttemptId) return;

    const targetAttempt = (Array.isArray(attempts) ? attempts : []).find(
      (attempt) => String(attempt?._id || '').trim() === pendingInitialAttemptId
    );

    if (!targetAttempt) return;

    setSelectedAttempt(targetAttempt);
    setViewMode('attempt');
    setPendingInitialAttemptId('');
  }, [attempts, pendingInitialAttemptId]);

  // Sync localCompletedAt with activeProject.completedAt
  useEffect(() => {
    const val = activeProject.completedAt;
    if (typeof val === 'string') {
      const dateObj = new Date(val);
      if (!isNaN(dateObj.getTime())) return setLocalCompletedAt(dateObj);
    }
    setLocalCompletedAt(null);
  }, [activeProject.completedAt]);

  if (!activeProject) return null;

  const sortedAttempts = (Array.isArray(attempts) ? attempts : [])
    .slice()
    .sort((a, b) => {
      const dateA = a.scheduledDate ? new Date(a.scheduledDate) : new Date(a.createdAt);
      const dateB = b.scheduledDate ? new Date(b.scheduledDate) : new Date(b.createdAt);
      return dateB - dateA;
    });

  const timingItems = [
    { label: 'Green', value: activeProject.green },
    { label: 'Amber', value: activeProject.amber },
    { label: 'Red', value: activeProject.red },
    { label: 'Total', value: activeProject.total },
  ].filter((item) => item.value);

  const manual = activeProject?.manual || {};
  const manualOverview = String(manual?.overview || '').trim();
  const manualReadingLevel = String(manual?.readingLevel || 'B1').trim().toUpperCase();
  const validReadingLevels = ['A1', 'A2', 'B1', 'B2'];
  const readingLevel = validReadingLevels.includes(manualReadingLevel) ? manualReadingLevel : 'B1';

  const sanitizeManualList = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  };

  const objectives = sanitizeManualList(manual?.objectives);
  const steps = sanitizeManualList(manual?.steps);
  const tips = sanitizeManualList(manual?.tips);
  const reflection = sanitizeManualList(manual?.reflection);

  const hasManualContent = Boolean(
    manualOverview || objectives.length || steps.length || tips.length || reflection.length
  );

  const renderItemCards = (items, emptyText) => {
    if (!Array.isArray(items) || items.length === 0) {
      return <div className="project-content-placeholder">{emptyText}</div>;
    }

    return (
      <div className="project-attempt-detail-list">
        {items.map((item, index) => {
          const itemTitle = item?.evaluatorName || item?.description || item?.text || item?.name || `Item ${index + 1}`;
          const itemBody = item?.feedback || item?.description || item?.text || item?.shortComment || item?.comment || '';

          return (
            <Card key={item?._id || `${itemTitle}-${index}`} className="project-attempt-detail-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="project-attempt-detail-card-title">{itemTitle}</div>
                {itemBody && itemBody !== itemTitle && (
                  <div className="project-attempt-detail-card-body">{String(itemBody)}</div>
                )}
                {!itemBody && (
                  <div className="project-attempt-detail-card-body project-attempt-detail-card-body--muted">No details available.</div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };

  const splitTopicName = (topicName) => {
    const name = String(topicName || '').trim();
    const match = name.match(/^(.*?)\s*\((.*)\)\s*$/);

    if (!match) {
      return { main: name, details: '' };
    }

    return {
      main: (match[1] || '').trim(),
      details: (match[2] || '').trim(),
    };
  };

  const renderStars = (count) => {
    const safe = Math.max(0, Math.min(5, Math.round(Number(count) || 0)));
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={index < safe ? 'evaluation-star evaluation-star--filled' : 'evaluation-star evaluation-star--empty'}
        aria-hidden="true"
      >
        {index < safe ? '★' : '☆'}
      </span>
    ));
  };

  const renderEvaluationCards = (evaluations, emptyText) => {
    if (!Array.isArray(evaluations) || evaluations.length === 0) {
      return <div className="project-content-placeholder">{emptyText}</div>;
    }

    return (
      <div className="project-attempt-detail-list">
        {evaluations.map((evaluation, evalIdx) => {
          const topics = Array.isArray(evaluation?.topics) ? evaluation.topics : [];
          const feedbackText = String(evaluation?.feedback || '').trim();
          const evaluatorName = String(evaluation?.evaluatorName || '').trim() || `Evaluator ${evalIdx + 1}`;

          return (
            <Card key={evaluation?._id || `${evaluatorName}-${evalIdx}`} className="project-attempt-detail-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="project-attempt-detail-card-title">{evaluatorName}</div>

                {feedbackText && (
                  <div className="project-attempt-detail-card-body">{feedbackText}</div>
                )}

                {topics.length > 0 ? (
                  <div className="project-evaluation-topic-list">
                    {topics.map((topic, topicIdx) => {
                      const parsed = splitTopicName(topic?.name);
                      const topicTitle = parsed.main || String(topic?.name || '').trim() || `Topic ${topicIdx + 1}`;
                      const topicStars = renderStars(topic?.stars);
                      const shortComment = String(topic?.shortComment || '').trim();

                      return (
                        <div key={`${evaluation?._id || evalIdx}-topic-${topicIdx}`} className="project-evaluation-topic-item">
                          <div className="project-evaluation-topic-top">
                            <div className="project-evaluation-topic-title">{topicTitle}</div>
                            {shortComment && (
                              <div className="project-evaluation-topic-comment project-evaluation-topic-comment--top">{shortComment}</div>
                            )}
                            <div className="project-evaluation-topic-stars" aria-label={`Rating ${Number(topic?.stars || 0)} of 5`}>
                              {topicStars}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="project-attempt-detail-card-body project-attempt-detail-card-body--muted">No topic ratings available.</div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.2rem', alignItems: 'center' }}>
        <Button variant="secondary" size="sm" onClick={onBack}>Back to Projects</Button>
        {(authUser.user.acl === 'Officer' || activeProject.userId === authUser.user._id) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontWeight: 500 }}>Completed at:</span>
            <DatePicker
              selected={localCompletedAt}
              onChange={async (date) => {
                setLocalCompletedAt(date);
                let newDate = '';
                if (date) {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  newDate = `${year}-${month}-${day}`;
                }
                setActiveProject({
                  ...activeProject,
                  completedAt: newDate || null,
                });
                try {
                  const response = await fetch(`${url}/user/${userPathway.user}/userPathway/${userPathway._id}/project/${activeProject.projectId}`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${authUser.token}`,
                    },
                    body: JSON.stringify({ completedAt: newDate || null }),
                  });
                  if (!response.ok) {
                    throw new Error('Failed to update completion date');
                  }
                  const updatedProject = await response.json();
                  if (!isMountedRef.current) return;
                  setActiveProject({
                    ...activeProject,
                    completedAt: updatedProject.completedAt,
                  });
                  toast.success(`Completion date has been updated successfully!`, { position: 'bottom-left' });
                } catch (error) {
                  console.error('Error updating scheduled date:', error);
                  toast.error('Failed to update completion date.', { position: 'bottom-left' });
                }
              }}
              dateFormat="yyyy-MM-dd"
              className="form-control custom-datepicker-input"
              placeholderText="Select date"
              isClearable
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              popperClassName="custom-datepicker-popper"
            />
          </div>
        )}
      </div>
      <Card className="mb-3 project-summary-card meetings-mock-card-shell meetings-page-card">
        <Card.Body>
          <h4 className="project-summary-title mb-2">{activeProject.title}</h4>
          {activeProject.label && activeProject.label !== activeProject.title && (
            <div className="project-summary-label mb-2">{activeProject.label}</div>
          )}
          {activeProject.description1 && (
            <div className="project-summary-description mb-2">{activeProject.description1}</div>
          )}
          {activeProject.description2 && (
            <div className="project-summary-description">{activeProject.description2}</div>
          )}
          {timingItems.length > 0 && (
            <div className="project-summary-timers mt-3">
              {timingItems.map((item) => (
                <span key={item.label} className={`project-summary-timer project-summary-timer--${String(item.label).toLowerCase()}`}>
                  {item.value}
                </span>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Button variant="info" size="sm" onClick={() => setViewMode('content')}>
          Open Project Content
        </Button>
      </div>

      {viewMode === 'content' ? (
        <Card className="mb-3 project-content-card meetings-mock-card-shell meetings-page-card">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Project Content</h5>
              <Button variant="secondary" size="sm" onClick={() => setViewMode('details')}>
                Back to Project Details
              </Button>
            </div>

            <div className="project-manual-header mb-3">
              <div>
                <div className="project-manual-kicker">Speaker Manual</div>
                <h6 className="project-manual-title mb-0">{activeProject.title}</h6>
              </div>
              <span className={`project-manual-reading-level project-manual-reading-level--${readingLevel.toLowerCase()}`}>
                Reading level {readingLevel}
              </span>
            </div>

            {hasManualContent ? (
              <div className="project-manual-sections">
                {manualOverview && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Overview</h6>
                    <p className="project-manual-overview mb-0">{manualOverview}</p>
                  </section>
                )}

                {objectives.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Objectives</h6>
                    <ul className="project-manual-list">
                      {objectives.map((item, index) => (
                        <li key={`objective-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {steps.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Steps</h6>
                    <ol className="project-manual-list project-manual-list--ordered">
                      {steps.map((item, index) => (
                        <li key={`step-${index}`}>{item}</li>
                      ))}
                    </ol>
                  </section>
                )}

                {tips.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Tips</h6>
                    <ul className="project-manual-list">
                      {tips.map((item, index) => (
                        <li key={`tip-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {reflection.length > 0 && (
                  <section className="project-manual-section">
                    <h6 className="project-manual-section-title">Reflection</h6>
                    <ul className="project-manual-list">
                      {reflection.map((item, index) => (
                        <li key={`reflection-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            ) : (
              <div className="project-content-placeholder">
                Manual content is not available for this project yet.
              </div>
            )}
          </Card.Body>
        </Card>
      ) : viewMode === 'attempt' && selectedAttempt ? (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button variant="secondary" size="sm" onClick={() => setViewMode('details')}>
              Back to Attempts
            </Button>
          </div>

          <div className="project-summary-card-wrapper mb-3">
            <Card className="project-summary-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="project-attempt-summary-top">
                  <div>
                    <h4 className="project-summary-title mb-2">{selectedAttempt.speechTitle || 'No title available'}</h4>
                    <div className="project-summary-label mb-2">
                      {selectedAttempt.scheduledDate ? new Date(selectedAttempt.scheduledDate).toISOString().slice(0, 10) : 'No date set'}
                    </div>
                  </div>
                  <div className="project-attempt-card-badges">
                    <span className="project-attempt-card-badge">{Array.isArray(selectedAttempt.feedbacks) ? selectedAttempt.feedbacks.length : 0} feedbacks</span>
                    <span className="project-attempt-card-badge">{Array.isArray(selectedAttempt.evaluations) ? selectedAttempt.evaluations.length : 0} evaluations</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="project-attempt-detail-grid mb-3">
            <Card className="project-attempt-detail-panel meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <h5 className="mb-3">Feedbacks</h5>
                {renderItemCards(selectedAttempt.feedbacks, 'No feedbacks available.')}
              </Card.Body>
            </Card>

            <Card className="project-attempt-detail-panel meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <h5 className="mb-3">Evaluations</h5>
                {renderEvaluationCards(selectedAttempt.evaluations, 'No evaluations available.')}
              </Card.Body>
            </Card>
          </div>
        </>
      ) : (
        <>
          {sortedAttempts.length > 0 ? (
            <div className="project-attempts-grid mb-3">
              {sortedAttempts.map((attempt) => {
                const feedbackCount = Array.isArray(attempt.feedbacks) ? attempt.feedbacks.length : 0;
                const evaluationCount = Array.isArray(attempt.evaluations) ? attempt.evaluations.length : 0;
                const scheduledDate = attempt.scheduledDate ? new Date(attempt.scheduledDate).toISOString().slice(0, 10) : 'No date set';

                return (
                  <Card key={attempt._id} className="project-attempt-card meetings-mock-card-shell meetings-page-card">
                    <Card.Body onClick={() => {
                      setSelectedAttempt(attempt);
                      setViewMode('attempt');
                    }} style={{ cursor: 'pointer' }}>
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                        <div>
                          <div className="project-attempt-card-title">{attempt.speechTitle || 'No title available'}</div>
                          <div className="project-attempt-card-date">{scheduledDate}</div>
                        </div>
                        <div className="project-attempt-card-badges">
                          <span className="project-attempt-card-badge">{feedbackCount} feedbacks</span>
                          <span className="project-attempt-card-badge">{evaluationCount} evaluations</span>
                        </div>
                      </div>
                      {attempt.feedbacks && attempt.feedbacks.length > 0 ? (
                        <div className="project-attempt-card-preview">
                          {String(attempt.feedbacks[0]?.description || attempt.feedbacks[0]?.text || 'No feedback text provided.')}
                        </div>
                      ) : (
                        <div className="project-attempt-card-preview project-attempt-card-preview--muted">
                          No feedbacks available.
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="mb-3 project-content-card meetings-mock-card-shell meetings-page-card">
              <Card.Body>
                <div className="project-content-placeholder">No attempts added yet.</div>
              </Card.Body>
            </Card>
          )}

        </>
      )}
    </div >
  );
}